/**
 * 钱包主页面
 * 包含余额显示、充值记录、消费记录等功能
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  Alert,
  FlatList,
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import {
  refreshAllData,
  selectRefreshing,
  fetchRechargeRecords,
  fetchSpendingRecords,
  selectRechargeRecords,
  selectSpendingRecords,
  selectRechargeLoading,
  selectSpendingLoading,
  selectRechargeHasMore,
  selectSpendingHasMore,
  initiateRecharge,
  completeRecharge,
} from '../store/walletSlice';
import { WalletBalanceCard } from './WalletBalanceCard';
import { WalletApiService } from '../services/walletApi';

type TabType = 'recharge' | 'spending';

interface RechargeRecordItemProps {
  record: any;
  onComplete?: (id: string) => void;
}

interface SpendingRecordItemProps {
  record: any;
}

const RechargeRecordItem: React.FC<RechargeRecordItemProps> = ({ record, onComplete }) => {
  return (
    <View style={styles.recordItem}>
      <View style={styles.recordHeader}>
        <Text style={styles.recordAmount}>
          +{WalletApiService.formatAmount(record.amount)}
        </Text>
        <View style={[styles.statusBadge, styles[`status_${record.status}`]]}>
          <Text style={styles.statusText}>
            {WalletApiService.getStatusText(record.status)}
          </Text>
        </View>
      </View>

      <View style={styles.recordDetails}>
        <Text style={styles.recordLabel}>支付方式: {record.payment_method}</Text>
        <Text style={styles.recordLabel}>
          创建时间: {WalletApiService.formatDate(record.created_at)}
        </Text>
        {record.completed_at && (
          <Text style={styles.recordLabel}>
            完成时间: {WalletApiService.formatDate(record.completed_at)}
          </Text>
        )}
        {record.transaction_id && (
          <Text style={styles.recordLabel}>
            交易ID: {record.transaction_id}
          </Text>
        )}
      </View>

      {record.status === 'pending' && onComplete && (
        <TouchableOpacity
          style={styles.completeButton}
          onPress={() => onComplete(record.id)}
        >
          <Text style={styles.completeButtonText}>模拟支付完成</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const SpendingRecordItem: React.FC<SpendingRecordItemProps> = ({ record }) => {
  return (
    <View style={styles.recordItem}>
      <View style={styles.recordHeader}>
        <Text style={[styles.recordAmount, styles.spendingAmount]}>
          -{WalletApiService.formatAmount(record.amount)}
        </Text>
        <Text style={styles.itemTypeText}>
          {WalletApiService.getItemTypeText(record.item_type)}
        </Text>
      </View>

      <View style={styles.recordDetails}>
        <Text style={styles.recordLabel}>描述: {record.description}</Text>
        <Text style={styles.recordLabel}>
          时间: {WalletApiService.formatDate(record.created_at)}
        </Text>
        {record.item_id && (
          <Text style={styles.recordLabel}>关联ID: {record.item_id}</Text>
        )}
      </View>
    </View>
  );
};

interface WalletScreenProps {
  onBackToChat?: () => void;
}

export const WalletScreen: React.FC<WalletScreenProps> = ({ onBackToChat }) => {
  const dispatch = useDispatch<AppDispatch>();
  const refreshing = useSelector((state: RootState) => selectRefreshing(state));

  const rechargeRecords = useSelector((state: RootState) => selectRechargeRecords(state));
  const spendingRecords = useSelector((state: RootState) => selectSpendingRecords(state));
  const rechargeLoading = useSelector((state: RootState) => selectRechargeLoading(state));
  const spendingLoading = useSelector((state: RootState) => selectSpendingLoading(state));
  const rechargeHasMore = useSelector((state: RootState) => selectRechargeHasMore(state));
  const spendingHasMore = useSelector((state: RootState) => selectSpendingHasMore(state));

  const [activeTab, setActiveTab] = useState<TabType>('recharge');

  React.useEffect(() => {
    // 初始加载数据
    dispatch(refreshAllData());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(refreshAllData());
  };

  const handleRecharge = () => {
    Alert.alert(
      '充值',
      '请选择充值金额',
      [
        { text: '取消', style: 'cancel' },
        { text: '¥10', onPress: () => initiateRechargeWithAmount(10) },
        { text: '¥50', onPress: () => initiateRechargeWithAmount(50) },
        { text: '¥100', onPress: () => initiateRechargeWithAmount(100) },
      ]
    );
  };

  const initiateRechargeWithAmount = (amount: number) => {
    dispatch(initiateRecharge({
      amount,
      paymentMethod: 'alipay',
    }))
    .unwrap()
    .then((response) => {
      Alert.alert(
        '充值请求成功',
        `充值金额: ¥${amount}\n订单ID: ${response.recharge_id}\n\n在实际应用中，这里会跳转到支付页面。现在可以点击记录中的"模拟支付完成"按钮来完成充值。`,
        [{ text: '确定' }]
      );
    })
    .catch((error) => {
      Alert.alert('充值失败', error, [{ text: '确定' }]);
    });
  };

  const handleCompleteRecharge = (rechargeId: string) => {
    Alert.alert(
      '确认支付',
      '确定要模拟完成这笔充值吗？',
      [
        { text: '取消', style: 'cancel' },
        {
          text: '确定',
          onPress: () => {
            dispatch(completeRecharge(rechargeId))
            .unwrap()
            .then(() => {
              Alert.alert('支付成功', '充值已完成！', [{ text: '确定' }]);
            })
            .catch((error) => {
              Alert.alert('支付失败', error, [{ text: '确定' }]);
            });
          },
        },
      ]
    );
  };

  const handleRecordsPress = () => {
    // 切换到充值记录标签页
    setActiveTab('recharge');
  };

  const loadMoreRechargeRecords = () => {
    if (rechargeHasMore && !rechargeLoading) {
      const page = Math.floor(rechargeRecords.length / 20) + 1;
      dispatch(fetchRechargeRecords({ page }));
    }
  };

  const loadMoreSpendingRecords = () => {
    if (spendingHasMore && !spendingLoading) {
      const page = Math.floor(spendingRecords.length / 20) + 1;
      dispatch(fetchSpendingRecords({ page }));
    }
  };

  return (
    <View style={styles.container}>
      {/* 顶部导航栏 */}
      <View style={styles.header}>
        {onBackToChat && (
          <TouchableOpacity style={styles.backButton} onPress={onBackToChat}>
            <Text style={styles.backButtonText}>← 返回</Text>
          </TouchableOpacity>
        )}
        <Text style={styles.headerTitle}>钱包</Text>
        <View style={styles.headerRight} />
      </View>

      <ScrollView
        style={styles.scrollView}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
      >
        {/* 余额卡片 */}
        <WalletBalanceCard
          onRechargePress={handleRecharge}
          onRecordsPress={handleRecordsPress}
        />

        {/* 记录标签页 */}
        <View style={styles.tabContainer}>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'recharge' && styles.activeTab]}
            onPress={() => setActiveTab('recharge')}
          >
            <Text style={[styles.tabText, activeTab === 'recharge' && styles.activeTabText]}>
              充值记录
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.tab, activeTab === 'spending' && styles.activeTab]}
            onPress={() => setActiveTab('spending')}
          >
            <Text style={[styles.tabText, activeTab === 'spending' && styles.activeTabText]}>
              消费记录
            </Text>
          </TouchableOpacity>
        </View>

        {/* 记录列表 */}
        <View style={styles.recordsContainer}>
          {activeTab === 'recharge' ? (
            <FlatList
              data={rechargeRecords}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <RechargeRecordItem
                  record={item}
                  onComplete={handleCompleteRecharge}
                />
              )}
              scrollEnabled={false}
              onEndReached={loadMoreRechargeRecords}
              onEndReachedThreshold={0.1}
              ListEmptyComponent={
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyText}>暂无充值记录</Text>
                </View>
              }
            />
          ) : (
            <FlatList
              data={spendingRecords}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => <SpendingRecordItem record={item} />}
              scrollEnabled={false}
              onEndReached={loadMoreSpendingRecords}
              onEndReachedThreshold={0.1}
              ListEmptyComponent={
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyText}>暂无消费记录</Text>
                </View>
              }
            />
          )}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    paddingTop: 50, // 为状态栏留出空间
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  backButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  backButtonText: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '600',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#000',
  },
  headerRight: {
    width: 60, // 为了保持标题居中
  },
  scrollView: {
    flex: 1,
  },

  // 标签页
  tabContainer: {
    flexDirection: 'row',
    marginHorizontal: 16,
    marginBottom: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    overflow: 'hidden',
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
  },
  activeTab: {
    backgroundColor: '#007AFF',
  },
  tabText: {
    fontSize: 16,
    color: '#666',
    fontWeight: '500',
  },
  activeTabText: {
    color: '#FFFFFF',
    fontWeight: '600',
  },

  // 记录列表
  recordsContainer: {
    marginHorizontal: 16,
    marginBottom: 20,
  },
  recordItem: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 16,
    marginBottom: 8,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  recordHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  recordAmount: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#34C759',
  },
  spendingAmount: {
    color: '#FF3B30',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  status_pending: {
    backgroundColor: '#FF9500',
  },
  status_completed: {
    backgroundColor: '#34C759',
  },
  status_failed: {
    backgroundColor: '#FF3B30',
  },
  status_cancelled: {
    backgroundColor: '#8E8E93',
  },
  statusText: {
    fontSize: 12,
    color: '#FFFFFF',
    fontWeight: '600',
  },
  itemTypeText: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '500',
  },
  recordDetails: {
    marginBottom: 8,
  },
  recordLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 2,
  },
  completeButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  completeButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },

  // 空状态
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
  },
});
